/**
 * Subscribes a signup form's email to a Klaviyo list, then lets — not
 * instead of — the form's normal Shopify submission proceed.
 *
 * sections/signup.liquid and sections/adhd-newsletter-signup.liquid only
 * load this file when both `klaviyo_public_api_key` and `klaviyo_list_id`
 * are set, and only render the `data-*` attributes this reads when they
 * are; the `{% form 'customer' %}` itself, its markup, and its styling are
 * all untouched. This listens for `submit`, reads the email field, and
 * fires Klaviyo's public client-side "Create Client Subscription" request
 * (developers.klaviyo.com/en/reference/create_client_subscription).
 *
 * The real (Shopify) submission is deliberately HELD, not left to race the
 * Klaviyo request: `Content-Type: application/vnd.api+json` is a
 * non-simple header, so every one of these requests needs a CORS preflight
 * first. A `fetch(..., {keepalive: true})` is not reliably delivered once
 * a preflight is involved and the page starts navigating away immediately
 * after — confirmed in practice: the Shopify signup succeeds (its own
 * success message renders) while the Klaviyo list never gains a member.
 * `event.preventDefault()` plus a capped wait (KLAVIYO_TIMEOUT_MS) for the
 * request to settle — or fail/time out — fixes that: the real submission
 * always still happens, just a beat later. `form.submit()` (not
 * `requestSubmit()`) is used to finish it, since the native `.submit()`
 * method does not re-dispatch a `submit` event and so can't re-enter this
 * same handler.
 */

const FORM_SELECTOR = '[data-ph-signup-klaviyo="true"]';
const EMAIL_FIELD_NAME = 'contact[email]';
const KLAVIYO_TIMEOUT_MS = 800;

/** @param {SubmitEvent} event */
function handleSubmit(event) {
  const form = /** @type {HTMLFormElement} */ (event.currentTarget);
  const publicKey = form.dataset.klaviyoPublicKey;
  const listId = form.dataset.klaviyoListId;
  if (!publicKey || !listId) return;

  const email = /** @type {HTMLInputElement | null} */ (
    form.elements.namedItem(EMAIL_FIELD_NAME)
  )?.value?.trim();
  if (!email) return;

  // Already holding this form's submission for an in-flight Klaviyo
  // request — let it finish rather than starting a second one.
  if (form.dataset.klaviyoPending === 'true') return;

  event.preventDefault();
  form.dataset.klaviyoPending = 'true';

  const body = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Website signup form',
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              subscriptions: {
                email: {
                  marketing: {
                    consent: 'SUBSCRIBED',
                  },
                },
              },
            },
          },
        },
      },
      relationships: {
        list: {
          data: {
            type: 'list',
            id: listId,
          },
        },
      },
    },
  };

  let settled = false;
  const finishSubmit = () => {
    if (settled) return;
    settled = true;
    form.submit();
  };

  const timeout = setTimeout(finishSubmit, KLAVIYO_TIMEOUT_MS);

  fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(publicKey)}`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/vnd.api+json',
      revision: '2026-07-15',
    },
    body: JSON.stringify(body),
  })
    .catch(() => {})
    .finally(() => {
      clearTimeout(timeout);
      finishSubmit();
    });
}

for (const form of document.querySelectorAll(FORM_SELECTOR)) {
  form.addEventListener('submit', handleSubmit);
}

// Sections loaded/re-rendered later by the theme editor (a merchant adding
// this section, or editing its settings) need the same listener — the
// module itself only runs once per page load.
document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  for (const form of target.querySelectorAll(FORM_SELECTOR)) {
    form.addEventListener('submit', handleSubmit);
  }
});
